import { useMemo } from 'react';

import { Spinner, Stack } from '@cast/design-system';
import { OmniEdgeLocationState } from '@cast/types-omni';

import { useAbility } from '@cast/shared-ability-data-access';
import { useCreateEdgeLocationDrawer } from 'features/cluster/autoscaler/edge-locations/hooks';
import { useGetClusterEdgeLocationsQuery } from 'hooks/queries/omni';

import { ProvisionNodesCloudConfigurationSlide } from './cloud-edge-location/ProvisionNodesCloudConfigurationSlide';
import { ProvisionNodesCustomConfigurationSlide } from './custom-edge-location/ProvisionNodesCustomConfigurationSlide';
import { EdgeLocationSelector } from './EdgeLocationSelector';
import { useProvisionNodesDrawerContext } from './ProvisionNodesDrawerContext';
import { EdgeLocationsEmptyState } from '../../_components/EdgeLocationsEmptyState';
import { CUSTOM_EDGE_PROVIDER } from '../../constants';

const HEADER_HEIGHT = 44;

export const ProvisionNodesConfigurationSlide = () => {
  const {
    clusterId,
    edgeLocation,
    hasEdgeLocationPredefined,
    setEdgeLocation,
  } = useProvisionNodesDrawerContext();
  const { edgeLocations, isLoading } = useGetClusterEdgeLocationsQuery({
    clusterId: clusterId ?? '',
    // Refetch interval is needed to dynamically show newly provisioned edge locations without refreshing the page
    refetchInterval: 15_000,
  });
  const { openCreateEdgeLocationDrawer } = useCreateEdgeLocationDrawer();
  const canCreate = useAbility().can('create', 'ClusterEdgeLocations');

  const isCustom = edgeLocation?.provider === CUSTOM_EDGE_PROVIDER;
  const eligibleEdgeLocations = useMemo(() => {
    if (!edgeLocations) {
      return [];
    }

    return edgeLocations.filter(
      (el) => el.state === OmniEdgeLocationState.READY
    );
  }, [edgeLocations]);

  if (isLoading) {
    return (
      <Stack
        sx={{ height: '100%', justifyContent: 'center', alignItems: 'center' }}
      >
        <Spinner color="primary" size={32} />
      </Stack>
    );
  }

  if (eligibleEdgeLocations.length === 0) {
    return (
      <Stack
        sx={{ height: '100%', justifyContent: 'center', alignItems: 'center' }}
      >
        <EdgeLocationsEmptyState
          title="Create edge locations and extend your cluster"
          testId="provision-nodes-no-edge-locations"
          actionLabel="Create edge location"
          hasPermission={canCreate}
          onActionClick={() =>
            openCreateEdgeLocationDrawer({
              clusterId: clusterId ?? '',
            })
          }
        />
      </Stack>
    );
  }

  return (
    <Stack sx={{ height: `calc(100% - ${HEADER_HEIGHT}px)` }}>
      <EdgeLocationSelector
        edgeLocation={edgeLocation}
        eligibleEdgeLocations={eligibleEdgeLocations}
        readOnly={hasEdgeLocationPredefined}
        onChange={(edgeLocation) => setEdgeLocation(edgeLocation ?? undefined)}
      />

      {edgeLocation && (
        <>
          {isCustom ? (
            <ProvisionNodesCustomConfigurationSlide
              key={edgeLocation?.id}
              edgeLocation={edgeLocation}
            />
          ) : (
            <ProvisionNodesCloudConfigurationSlide
              key={edgeLocation?.id}
              edgeLocation={edgeLocation}
            />
          )}
        </>
      )}
    </Stack>
  );
};
